import { BookCallButton } from "@/components/shared/BookCallButton";
import { DealerAdvantage } from "@/components/shared/DealerAdvantage";
import { DealerTestimonials } from "@/components/shared/DealerTestimonials";
import { ContactTimePicker, formatContactTime } from "@/components/shared/ContactTimePicker";
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { SEOHead } from "@/components/shared/SEOHead";
import { InternalLinks } from "@/components/shared/InternalLinks";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { SEOExpansion } from "@/components/shared/SEOExpansion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { submitDealerApplication, getPageContent } from "@/lib/api";
import { getSubmitSource } from "@/lib/attribution";
import { motion } from "framer-motion";
import { CheckCircle, ArrowRight, Loader2 } from "lucide-react";
import { AwardsSection } from "@/components/shared/AwardsSection";
import { WhyChoosePmw } from "@/components/shared/WhyChoosePmw";

const usStates = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming"
];

const STORE_CONTACT_FIELDS = [
  { name: "store_phone", label: "Store Phone (optional)", type: "tel", required: false },
  { name: "store_email", label: "Store Email (optional)", type: "email", required: false },
  { name: "prescreen_business", label: "Have you previously operated a business?", type: "select", options: ["Yes", "No"], required: true },
  { name: "prescreen_bank_ref", label: "Can you give a banking reference for a business you operated?", type: "select", options: ["Yes", "No"], required: true },
  { name: "prescreen_pro_ref", label: "Can you share a professional reference from someone you've worked with?", type: "select", options: ["Yes", "No"], required: true },
  { name: "prescreen_wireless", label: "Do you currently run a wireless retail business?", type: "select", options: ["Yes", "No"], required: true },
];

const DEFAULT_FORM_FIELDS = [
  { name: "first_name", label: "First Name", type: "text", required: true },
  { name: "last_name", label: "Last Name", type: "text", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "phone", label: "Phone", type: "tel", required: true },
  { name: "company_name", label: "Company Name", type: "text", required: true },
  { name: "business_address", label: "Business Address", type: "text", required: true },
  { name: "city", label: "City", type: "text", required: true },
  { name: "state", label: "State", type: "select", required: true },
  { name: "zip_code", label: "ZIP Code", type: "text", required: true },
  ...STORE_CONTACT_FIELDS,
  { name: "years_experience", label: "Years in Wireless Retail", type: "number", required: true },
  { name: "current_locations", label: "Current Store Locations", type: "number", required: false },
  { name: "investment_capital", label: "Available Investment Capital", type: "select", required: true, options: ["$50,000 - $100,000", "$100,000 - $200,000", "$200,000 - $500,000", "$500,000+"] },
  { name: "message", label: "Additional Information", type: "textarea", required: false },
];

function getSection(sections, name, fallback = {}) {
  const found = sections.find((s) => s.section_name === name && s.active !== false);
  if (!found) return fallback;
  return { ...fallback, ...found, metadata: { ...(fallback.metadata || {}), ...(found.metadata || {}) } };
}

function DynamicField({ field, value, onChange, onSelectChange }) {
  if (field.type === "textarea") {
    return (
      <div className={field.name === "message" ? "sm:col-span-2" : ""}>
        <Label htmlFor={field.name}>{field.label}{field.required ? " *" : ""}</Label>
        <Textarea
          id={field.name}
          name={field.name}
          value={value || ""}
          onChange={onChange}
          required={field.required}
          placeholder={field.placeholder || `Tell us about your business goals...`}
          rows={4}
          data-testid={`input-${field.name}`}
        />
      </div>
    );
  }

  if (field.type === "select" && field.name === "state") {
    return (
      <div>
        <Label htmlFor={field.name}>{field.label}{field.required ? " *" : ""}</Label>
        <Select value={value || ""} onValueChange={(v) => onSelectChange(field.name, v)}>
          <SelectTrigger data-testid={`select-${field.name}`}><SelectValue placeholder="Select state" /></SelectTrigger>
          <SelectContent>{usStates.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    );
  }

  if (field.type === "select" && field.options) {
    return (
      <div className="sm:col-span-2">
        <Label htmlFor={field.name}>{field.label}{field.required ? " *" : ""}</Label>
        <Select value={value || ""} onValueChange={(v) => onSelectChange(field.name, v)}>
          <SelectTrigger data-testid={`select-${field.name}`}><SelectValue placeholder={field.options.length === 2 ? "Select Yes or No" : "Select an option"} /></SelectTrigger>
          <SelectContent>{field.options.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    );
  }

  const spanClass = ["company_name", "business_address"].includes(field.name) ? "sm:col-span-2" : "";
  return (
    <div className={spanClass}>
      <Label htmlFor={field.name}>{field.label}{field.required ? " *" : ""}</Label>
      <Input
        id={field.name}
        name={field.name}
        type={field.type || "text"}
        value={value || ""}
        onChange={onChange}
        required={field.required}
        min={field.type === "number" ? "0" : undefined}
        placeholder={field.placeholder || ""}
        data-testid={`input-${field.name}`}
      />
    </div>
  );
}

export default function BecomeDealer() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [loading, setLoading] = useState(false);
  const [contactTime, setContactTime] = useState({ date: "", time: "" });
  const [formData, setFormData] = useState({});
  const [sections, setSections] = useState([]);

  useEffect(() => { getPageContent("become-dealer").then(setSections).catch(() => {}); }, []);

  // Smooth-scroll to #apply when SEO anchor links arrive on this page
  useEffect(() => {
    if (routerLocation.hash !== "#apply") return;
    const t = setTimeout(() => {
      const el = document.getElementById("apply");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(t);
  }, [routerLocation.hash, sections]);

  const hero = getSection(sections, "hero", { title: "Become a Total Wireless Dealer — Open or Expand Your Cell Phone Store", content: "Partner with Prime Media Wireless, a Total Wireless Master Agent helping entrepreneurs launch, convert, and grow wireless retail businesses nationwide.", metadata: { subtitle: "Join Our Network" } });
  const reqs = getSection(sections, "requirements", { title: "Dealer Requirements", content: "To become a Total Wireless exclusive dealer, you should meet the following qualifications:", metadata: { items: [], contact_email: "info@primemediawireless.com", contact_phone: "1-800-PRIME-MW" } });

  const reqItems = reqs.metadata?.items || [];
  // Depend on the stable form_fields array ref (reqs is a fresh object every render)
  const cmsFieldsRef = reqs.metadata?.form_fields;
  const formFields = useMemo(() => {
    const cmsFields = cmsFieldsRef;
    if (!(Array.isArray(cmsFields) && cmsFields.length > 0)) return DEFAULT_FORM_FIELDS;
    const missing = STORE_CONTACT_FIELDS.filter((f) => !cmsFields.some((c) => c.name === f.name));
    if (!missing.length) return cmsFields;
    const at = cmsFields.findIndex((c) => c.name === "zip_code");
    return at < 0 ? [...cmsFields, ...missing] : [...cmsFields.slice(0, at + 1), ...missing, ...cmsFields.slice(at + 1)];
  }, [cmsFieldsRef]);

  useEffect(() => {
    const initial = {};
    formFields.forEach((f) => { initial[f.name] = ""; });
    setFormData((prev) => ({ ...initial, ...Object.fromEntries(Object.entries(prev || {}).filter(([k]) => k in initial)) }));
  }, [formFields]);

  const handleChange = (e) => { setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value })); };
  const handleSelectChange = (name, value) => { setFormData((prev) => ({ ...prev, [name]: value })); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = formFields.filter((f) => f.required && f.type === "select" && !String(formData[f.name] ?? "").trim());
    if (missing.length) { toast.error(`Please answer: ${missing.map((f) => f.label).join("; ")}`); return; }
    setLoading(true);
    try {
      const known = new Set(["first_name", "last_name", "email", "phone", "company_name", "business_address", "city", "state", "zip_code",
        "years_experience", "current_locations", "investment_capital", "message", "store_phone", "store_email"]);
      const submission = {};
      const extra = {};
      Object.entries(formData).forEach(([k, v]) => { if (known.has(k)) submission[k] = v; else if (String(v ?? "").trim()) extra[formFields.find((f) => f.name === k)?.label || k] = String(v); });
      if (Object.keys(extra).length) submission.extra_answers = extra;
      const when = formatContactTime(contactTime);
      if (when) submission.extra_answers = { ...(submission.extra_answers || {}), "Best time to reach": when };
      submission.submit_source = getSubmitSource();
      await submitDealerApplication(submission);
      toast.success("Application submitted successfully! We'll be in touch soon.");
      navigate("/");
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => `${x.loc?.slice(-1)[0] || "field"}: ${x.msg}`).join("; ") : (typeof d === "string" ? d : "Failed to submit application"));
    }
    finally { setLoading(false); }
  };

  const nonTextareaFields = formFields.filter((f) => f.type !== "textarea");
  const textareaFields = formFields.filter((f) => f.type === "textarea");

  return (
    <PublicLayout>
      <SEOHead
        pageSlug="become-dealer"
        fallback={{
          meta_title: "Total Wireless Master Agent & Dealer Opportunities | Prime Media Wireless",
          meta_description: "Become a Total Wireless dealer with Prime Media Wireless. Get help opening your wireless store, dealer training, competitive commissions, and nationwide support.",
          canonical_url: `${window.location.origin}/become-dealer`,
        }}
      />
      <section className="bg-navy py-16" data-testid="become-dealer-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="label-style text-teal mb-4">{hero.metadata?.subtitle}</p>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl text-white mb-6">{hero.title}</h1>
            <p className="text-slate-300 text-lg font-body">{hero.content}</p>
            <BookCallButton className="mt-8" />
          </div>
        </div>
      </section>

      {/* Awards banner directly under the hero so #1 Growth Champion leads the pitch */}
      <AwardsSection compact heading="A 2026 Master Agent Award Winner" subheading="Prime Media Wireless is the #1 Growth Master Agent for Total Wireless (Verizon Value)." />

      {/* Six-pillar "Why Choose PMW" — puts leadership access, field support, and enterprise onboarding front and center before the form */}
      <WhyChoosePmw heading="Why Dealers Choose Prime Media Wireless" subheading="Six reasons ambitious operators are moving their stores under the #1 Growth Master Agent for Total Wireless." />

      <section id="apply" className="py-16 bg-slate-50 scroll-mt-24" data-testid="become-dealer-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="lg:col-span-4">
              <div className="sticky top-24">
                <h2 className="font-heading font-bold text-2xl text-navy mb-4">{reqs.title}</h2>
                <p className="text-slate-600 mb-6 font-body">{reqs.content}</p>
                <ul className="space-y-3">
                  {reqItems.map((req) => (
                    <li key={req} className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" /><span className="text-slate-700 text-sm font-body">{req}</span></li>
                  ))}
                </ul>
                {(reqs.metadata?.contact_email || reqs.metadata?.contact_phone) && (
                  <div className="mt-8 p-4 bg-teal/10 rounded-lg border border-teal/20">
                    <p className="text-navy font-semibold font-heading mb-2">Questions?</p>
                    <p className="text-slate-600 text-sm font-body">
                      Contact us at <span className="text-teal">{reqs.metadata?.contact_email}</span>
                      {reqs.metadata?.contact_phone && <> or call {reqs.metadata.contact_phone}</>}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="lg:col-span-8">
              <div className="bg-white rounded-xl border border-slate-200 p-6 lg:p-8">
                <h2 className="font-heading font-bold text-2xl text-navy mb-6">Dealer Application</h2>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="dealer-application-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    {nonTextareaFields.map((field) => (
                      <DynamicField
                        key={field.name}
                        field={field}
                        value={formData[field.name]}
                        onChange={handleChange}
                        onSelectChange={handleSelectChange}
                      />
                    ))}
                  </div>
                  {textareaFields.map((field) => (
                    <DynamicField
                      key={field.name}
                      field={field}
                      value={formData[field.name]}
                      onChange={handleChange}
                      onSelectChange={handleSelectChange}
                    />
                  ))}
                  <ContactTimePicker value={contactTime} onChange={setContactTime} />
                  <Button type="submit" disabled={loading} className="w-full bg-teal hover:bg-teal-dark text-white font-semibold" size="lg" data-testid="submit-application-btn">
                    {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Submitting...</> : <>Submit Application<ArrowRight className="w-5 h-5 ml-2" /></>}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      <DealerAdvantage cta={false} />
      <DealerTestimonials />
      <SEOExpansion withAdvantage={false} />
      <InternalLinks exclude="/become-dealer" />
    </PublicLayout>
  );
}

export { usStates };
